/**
 * Turning an audit row into a sentence.
 *
 * Pure, and separate from the screen, because the interesting property is
 * testable: an action this build has never seen must still render as something
 * a person can read, and no branch may ever print a value that could be a
 * secret.
 *
 * The rule for what reaches the screen: names, role names, statuses and
 * capability codes — all of which the reader can already see elsewhere in the
 * product. Never a token, never a key, never free text somebody typed about a
 * customer. `metadata` is written by the database's own triggers and by
 * `write_audit_for`, and nothing writes anything else into it; this function is
 * the second line, and it reads named fields rather than printing the object.
 */

import { RANK_WORDS as RANK } from '@timely/domain';

export interface AuditRow {
    id: number;
    actor_name: string;
    actor_user_id: string | null;
    action: string;
    target_kind: string | null;
    target_id: string | null;
    metadata: Record<string, unknown> | null;
    created_at: string;
}

export interface DescribedEntry {
    /** What happened, with who did it at the front. */
    sentence: string;
    /** The part worth a smaller line: which capabilities, which role. */
    detail: string | null;
    /** A short label for the badge. */
    kind: string;
}

const text = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** What a vault row was, in words. */
const secretName = (meta: Record<string, unknown>): string =>
    meta['kind'] === 'google_oauth' ? 'the Google connection' : 'the AI key';

const PROVIDER_WORDS: Record<string, string> = { google: 'Google', openai: 'OpenAI', anthropic: 'Anthropic' };
const providerDetail = (meta: Record<string, unknown>): string | null => {
    const provider = text(meta['provider']);
    return provider ? `Provider: ${PROVIDER_WORDS[provider] ?? provider}.` : null;
};

/** A key version, which the database writes as a number. */
const version = (value: unknown): string =>
    typeof value === 'number' && Number.isInteger(value) ? String(value) : text(value) ?? '?';

/** A payment rank in the words the rank control and the history use. */
const RANK_WORDS: Record<string, string> = { ...RANK, automatic: 'automatic' };
const rankWords = (value: unknown): string => RANK_WORDS[text(value) ?? 'automatic'] ?? text(value) ?? 'automatic';

/** The person's name, or an honest stand-in. Never a raw user id. */
const actorOf = (row: AuditRow): string => text(row.actor_name) ?? 'Somebody';

export function describeAuditEntry(row: AuditRow): DescribedEntry {
    const who = actorOf(row);
    const meta = row.metadata ?? {};
    const name = text(meta['name']);
    const target = name ?? text(row.target_id) ?? 'it';

    switch (row.action) {
        case 'workspace.created':
            return { sentence: `${who} created ${name ?? 'this company'}.`, detail: null, kind: 'Company' };

        // One change to many accounts (0033). Counts and the kind of change only;
        // which accounts changed is in each account's own history.
        case 'customers.bulk_changed': {
            const changed = typeof meta['changed'] === 'number' ? meta['changed'] : 0;
            const what = { collector: 'reassigned', follow_up: 'set the next follow-up on', rank: 'set the payment rank of' }[text(meta['change']) ?? ''] ?? 'changed';
            const value = text(meta['value']);
            const left = (typeof meta['requested'] === 'number' ? meta['requested'] : changed) - changed;
            return {
                sentence: `${who} ${what} ${changed} ${changed === 1 ? 'account' : 'accounts'} at once.`,
                detail: [
                    value ? `To: ${value === 'owner' ? 'each account’s owner' : meta['change'] === 'rank' ? rankWords(value) : value}.` : null,
                    left > 0 ? `${left} left as they were.` : null,
                ].filter(Boolean).join(' ') || null,
                kind: 'Accounts',
            };
        }
        // One account's rank (0033): every rank change leaves one of these and a history line.
        case 'customers.rank_changed':
            return {
                sentence: `${who} set the payment rank of ${text(meta['company']) ?? 'an account'} to ${rankWords(meta['to'])}.`,
                detail: `It was ${rankWords(meta['from'])}.`,
                kind: 'Accounts',
            };
        case 'cheques.bulk_changed': {
            const changed = typeof meta['changed'] === 'number' ? meta['changed'] : 0;
            const status = text(meta['status']);
            const words = { Cleared: 'marked cleared', Hold: 'put on hold', Bounced: 'marked bounced', Pending: 'put back in hand' }[status ?? ''] ?? 'changed';
            return {
                sentence: `${who} ${words} ${changed} ${changed === 1 ? 'cheque' : 'cheques'} at once.`,
                detail: null,
                kind: 'Cheques',
            };
        }

        case 'member.invited':
            return {
                sentence: `${who} created an invitation for ${text(row.target_id) ?? 'somebody'}.`,
                detail: null, kind: 'Invitation',
            };
        case 'member.invite_sent':
            return {
                sentence: `${who} invited ${text(row.target_id) ?? 'somebody'}.`,
                detail: 'An email was sent.', kind: 'Invitation',
            };
        case 'member.invite_resent':
            return {
                sentence: `${who} sent the invitation to ${text(row.target_id) ?? 'somebody'} again.`,
                detail: 'The earlier link stopped working.', kind: 'Invitation',
            };
        case 'member.invite_revoked':
            return { sentence: `${who} withdrew an invitation.`, detail: null, kind: 'Invitation' };
        case 'member.joined':
            return {
                sentence: `${text(row.target_id) ?? 'Somebody'} accepted an invitation and joined.`,
                detail: null, kind: 'People',
            };

        // A person changing their own name (0037): both names, which the team sees anyway.
        case 'member.renamed': {
            const from = text(meta['from']);
            const to = text(meta['to']);
            return {
                sentence: from && to ? `${to} changed their name from “${from}”.` : `${who} changed their name.`,
                detail: null, kind: 'People',
            };
        }

        // The starter templates, given once by release 0037 to a company that had none.
        case 'templates.defaults_added': {
            const added = typeof meta['count'] === 'number' ? meta['count'] : null;
            return {
                sentence: `Timely added ${added ?? 'the'} starter message templates. They are this company’s to edit or delete.`,
                detail: null, kind: 'Templates',
            };
        }

        case 'member.added':
            return { sentence: `${who} added ${target} to the company.`, detail: null, kind: 'People' };
        case 'member.removed':
            return { sentence: `${who} removed ${target} from the company.`, detail: null, kind: 'People' };
        case 'member.role_changed':
            return { sentence: `${who} moved ${target} to a different role.`, detail: null, kind: 'People' };
        case 'member.status_changed': {
            const to = text(meta['to']);
            return {
                sentence: to === 'suspended'
                    ? `${who} suspended ${target}.`
                    : to === 'active'
                        ? `${who} reinstated ${target}.`
                        : `${who} changed ${target}’s status.`,
                detail: null, kind: 'People',
            };
        }

        case 'role.created':
            return {
                sentence: `${who} created the role ${target}.`,
                detail: describeCapabilities(list(meta['capabilities'])),
                kind: 'Roles',
            };
        case 'role.deleted':
            return { sentence: `${who} deleted the role ${target}.`, detail: null, kind: 'Roles' };
        case 'role.changed': {
            const granted = list(meta['granted']);
            const revoked = list(meta['revoked']);
            const renamed = text(meta['renamed']);
            const parts: string[] = [];
            if (granted.length) parts.push(`Gained ${granted.join(', ')}.`);
            if (revoked.length) parts.push(`Lost ${revoked.join(', ')}.`);
            if (renamed) parts.push(`Renamed from ${renamed}.`);
            return {
                sentence: `${who} changed the role ${target}.`,
                detail: parts.length ? parts.join(' ') : null,
                kind: 'Roles',
            };
        }

        case 'alerts.settings_changed': {
            const parts: string[] = [];
            if (typeof meta['daily_email'] === 'boolean') parts.push(meta['daily_email'] ? 'Switched on.' : 'Switched off.');
            const at = text(meta['send_at_local']);
            if (at) parts.push(`Send time ${at}.`);
            if (typeof meta['recipient_roles'] === 'number') parts.push(`${meta['recipient_roles']} recipient role(s).`);
            if (typeof meta['extra_recipients'] === 'number') parts.push(`${meta['extra_recipients']} extra address(es).`);
            if (typeof meta['skip_when_empty'] === 'boolean') {
                parts.push(meta['skip_when_empty'] ? 'Skips empty days.' : 'Sends on empty days.');
            }
            return {
                sentence: `${who} changed the reminder digest settings.`,
                detail: parts.length ? parts.join(' ') : null,
                kind: 'Reminders',
            };
        }
        case 'alerts.test_sent': {
            const status = text(meta['status']);
            return {
                sentence: `${who} sent themselves a test digest.`,
                detail: status === 'sent' ? 'It was delivered.' : status ? `It was not sent (${status}).` : null,
                kind: 'Reminders',
            };
        }

        // The vault. None of these rows carries any part of a credential — the
        // database writes only the kind, the provider and a key version — so
        // there is nothing here to redact, only to name.
        case 'secret.configured':
            return { sentence: `${who} saved ${secretName(meta)}.`, detail: providerDetail(meta), kind: 'Credentials' };
        case 'secret.replaced':
            return { sentence: `${who} replaced ${secretName(meta)}.`, detail: providerDetail(meta), kind: 'Credentials' };
        case 'secret.removed':
            return { sentence: `${who} removed ${secretName(meta)}.`, detail: providerDetail(meta), kind: 'Credentials' };
        case 'secret.verified':
            return { sentence: `${who} checked ${secretName(meta)}: it works.`, detail: null, kind: 'Credentials' };
        case 'secret.verification_failed':
            return {
                sentence: `${who} checked ${secretName(meta)}: it did not work.`,
                detail: text(meta['category'])?.replace(/_/g, ' ') ?? null,
                kind: 'Credentials',
            };
        // AI customer reports. The row names the model and versions; the
        // report itself, its prompt and the key are never in it.
        case 'ai.report_generated':
            return {
                sentence: `${who} asked for an AI summary of an account.`,
                detail: text(meta['model']) ? `Written by ${text(meta['model'])}.` : null,
                kind: 'AI',
            };
        case 'ai.report_failed':
            return {
                sentence: `${who} asked for an AI summary of an account; it was not written.`,
                detail: text(meta['category'])?.replace(/_/g, ' ') ?? null,
                kind: 'AI',
            };
        case 'secret.rekeyed':
            return {
                sentence: `Timely Payment re-encrypted ${secretName(meta)} under a new master key.`,
                detail: `Key version ${version(meta['from_key_version'])} → ${version(meta['to_key_version'])}. Nothing about the credential changed.`,
                kind: 'Credentials',
            };

        // TallyPrime and its connector (0034–0036). Named in words rather than printed as codes and ids; the
        // fields read are the computer's and the TallyPrime company's names, which the team sees on Receivables data.
        case 'connector.pairing_code_created':
            return { sentence: `${who} made a pairing code for Timely Connector.`, detail: null, kind: 'TallyPrime' };
        case 'connector.paired':
            return {
                sentence: `${who} paired Timely Connector${text(meta['device']) ? ` on ${text(meta['device'])}` : ''}.`,
                detail: null, kind: 'TallyPrime',
            };
        case 'connector.company_chosen':
            return {
                sentence: `${who} chose the TallyPrime company${text(meta['company']) ? ` ${text(meta['company'])}` : ''}.`,
                detail: null, kind: 'TallyPrime',
            };
        case 'connector.activated':
            return {
                sentence: `${who} connected TallyPrime${text(meta['company']) ? ` (${text(meta['company'])})` : ''}.`,
                detail: text(meta['device']) ? `Read by Timely Connector on ${text(meta['device'])}.` : null,
                kind: 'TallyPrime',
            };
        case 'connector.renamed':
            return { sentence: `${who} renamed the connector’s computer${text(meta['device']) ? ` to ${text(meta['device'])}` : ''}.`, detail: null, kind: 'TallyPrime' };
        case 'connector.revoked':
            return { sentence: `${who} disconnected Timely Connector.`, detail: null, kind: 'TallyPrime' };
        case 'connector.rotation_requested':
            return { sentence: `${who} asked Timely Connector to renew its credential.`, detail: null, kind: 'TallyPrime' };
        case 'connector.settlement_approved':
            return { sentence: `${who} approved an update from TallyPrime that had been held for review.`, detail: null, kind: 'TallyPrime' };
        case 'tally.pilot_enabled':
            return { sentence: 'Timely Payment switched TallyPrime on for this company.', detail: null, kind: 'TallyPrime' };
        case 'tally.pilot_disabled':
            return { sentence: 'Timely Payment switched TallyPrime off for this company.', detail: null, kind: 'TallyPrime' };
        // The ageing windows (0036). The version only — the reason somebody typed stays on Receivables data.
        case 'receivables.settings.saved':
            return {
                sentence: `${who} saved the ageing settings${typeof meta['version'] === 'number' ? ` (version ${meta['version']})` : ''}.`,
                detail: null, kind: 'Receivables',
            };

        default:
            // An action from a newer migration than this build. Shown as
            // itself: an audit log that silently drops what it does not
            // recognise is worse than one that prints a bare code.
            return {
                sentence: `${who} — ${row.action}`,
                detail: text(row.target_id),
                kind: 'Other',
            };
    }
}

const describeCapabilities = (capabilities: string[]): string | null =>
    capabilities.length === 0
        ? 'No permissions.'
        : `${capabilities.length} permission${capabilities.length === 1 ? '' : 's'}: ${capabilities.join(', ')}.`;
